import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import {
  ChangeInteressentskabKind,
  InterestPartyChangeDirection,
  JaNej,
} from "../model/registrationEnums";
import { emptyChangeInterestParty } from "../model/registrationInitialValues";
import {
  SelectFormField,
  TextFormField,
} from "@blanket/components/formFields/FormFields";
import { ChangeInteressentskabEnteringPartyEligibilityFields } from "../components/eligibility/ChangeInteressentskabEnteringPartyEligibilityFields";
import { LeavingPartyFields } from "../components/eligibility/LeavingPartyFields";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const ChangeInteressentskabSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const changeInteressentskab = values.changeInteressentskab;
  const nameLookup = useCompanyNameLookup({
    name: "changeInteressentskab.name",
    cvr: "changeInteressentskab.cvr",
    pNumber: "changeInteressentskab.pNumber",
  });
  const showInterestPartyDirection =
    changeInteressentskab.changeKind ===
    ChangeInteressentskabKind.InteressentBegge;

  return (
    <>
      <FormSection
        id="section-change-is-info"
        number={2}
        title="Stamoplysninger"
        description="Stamoplysninger om interessentskabet, der ændres."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyNameSuggestions
            items={nameLookup.data}
            isLoading={nameLookup.isLoading}
            onSelect={nameLookup.select}
            onDismiss={nameLookup.dismiss}
          >
            <TextFormField
              name="changeInteressentskab.name"
              label="Navn på virksomhed"
              id="change-is-input-name"
              isRequired
            />
          </CompanyNameSuggestions>
          <div className="blanket-two-col">
            <TextFormField
              name="changeInteressentskab.cvr"
              label="CVR-nr."
              id="change-is-input-cvr"
              isRequired
              type="number"
            />
            <TextFormField
              name="changeInteressentskab.pNumber"
              label="P-nummer"
              id="change-is-input-p-number"
              isRequired
              type="number"
            />
          </div>
        </div>
      </FormSection>

      <FormSection
        id="section-change-is-details"
        number={3}
        title="Oplysninger om interessenter"
        description="Type af ændring og interessentoplysninger."
      >
        <div className="d-flex flex-column gap-4">
          <SelectFormField
            name="changeInteressentskab.changeKind"
            label="Hvilken type ændring skal registreres?"
            isRequired
            helperText="Der skal være mindst to deltagere i interessentskabet. Alle deltagere skal være bestallingshavende advokater eller registrerede advokatselskaber."
            id="change-is-input-change-kind"
            options={[
              {
                value: ChangeInteressentskabKind.InteressentInd,
                label: "En interessent indtræder",
              },
              {
                value: ChangeInteressentskabKind.InteressentUd,
                label: "En interessent udtræder",
              },
              {
                value: ChangeInteressentskabKind.InteressentBegge,
                label: "Begge dele",
              },
            ]}
          />
          <SubGroupPanel
            className="blanket-participant-subsection"
            title="Oplysninger om deltageren"
          >
            <FieldArray name="changeInteressentskab.interestParties">
              {({ push, remove }) => (
                <FieldGroup className="blanket-participant-list">
                  {changeInteressentskab.interestParties.map((party, index) => {
                    const isEnters =
                      changeInteressentskab.changeKind ===
                        ChangeInteressentskabKind.InteressentInd ||
                      (showInterestPartyDirection &&
                        party.direction ===
                          InterestPartyChangeDirection.Enters);
                    const isLeaves =
                      changeInteressentskab.changeKind ===
                        ChangeInteressentskabKind.InteressentUd ||
                      (showInterestPartyDirection &&
                        party.direction ===
                          InterestPartyChangeDirection.Leaves);

                    return (
                      <div
                        key={party._rowId}
                        className="blanket-participant-card"
                      >
                        <div className="d-flex flex-column gap-4">
                          {showInterestPartyDirection && (
                            <SelectFormField
                              name={`changeInteressentskab.interestParties.${index}.direction`}
                              label="Træder interessenten ind eller ud?"
                              id={`change-is-party-direction-${index}`}
                              isRequired
                              options={[
                                {
                                  value: InterestPartyChangeDirection.Enters,
                                  label: "Ind",
                                },
                                {
                                  value: InterestPartyChangeDirection.Leaves,
                                  label: "Ud",
                                },
                              ]}
                            />
                          )}
                          <div className="blanket-two-col">
                            <TextFormField
                              name={`changeInteressentskab.interestParties.${index}.name`}
                              label="Navn"
                              id={`change-is-party-name-${index}`}
                              isRequired
                            />
                            <TextFormField
                              name={`changeInteressentskab.interestParties.${index}.cvrCpr`}
                              label="CVR/CPR"
                              id={`change-is-party-cvr-${index}`}
                              isRequired
                            />
                          </div>
                          {isLeaves && (
                            <LeavingPartyFields
                              namePrefix={`changeInteressentskab.interestParties.${index}`}
                              idPrefix={`change-is-party-${index}`}
                              participantType={party.participantType}
                              withdrawalReason={party.withdrawalReason}
                            />
                          )}
                          {isEnters && (
                            <ChangeInteressentskabEnteringPartyEligibilityFields
                              namePrefix={`changeInteressentskab.interestParties.${index}`}
                              idPrefix={`change-is-party-${index}`}
                              isLawyerOrLawFirm={party.isLawyerOrLawFirm}
                              passedPartnerExam={party.passedPartnerExam}
                              leavesOtherOnEntry={party.leavesOtherOnEntry}
                            />
                          )}
                          {isEnters &&
                            party.leavesOtherOnEntry === JaNej.Ja && (
                              <>
                                <TextFormField
                                  name={`changeInteressentskab.interestParties.${index}.otherCompanyName`}
                                  label="Navn på virksomhed"
                                  id={`change-is-party-other-company-name-${index}`}
                                />
                                <TextFormField
                                  name={`changeInteressentskab.interestParties.${index}.otherCompanyCvr`}
                                  label="CVR-nr."
                                  id={`change-is-party-other-company-cvr-${index}`}
                                />
                              </>
                            )}
                        </div>

                        <div className="mt-3 d-flex justify-content-end">
                          <TooltippedRemoveButton
                            onClick={() => remove(index)}
                            ariaLabel="Fjern"
                            disabled={
                              changeInteressentskab.interestParties.length <= 1
                            }
                            tooltipContent="Mindst én skal angives"
                          >
                            Fjern
                          </TooltippedRemoveButton>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => push(emptyChangeInterestParty())}
                    aria-label="Tilføj interessent"
                  >
                    Tilføj interessent
                  </Button>
                </FieldGroup>
              )}
            </FieldArray>
          </SubGroupPanel>
        </div>
      </FormSection>

      <FormSection id="section-change-is-date" number={4} title="Ændringsdato">
        <div className="mb-4">
          <RegistrationRemarksField
            namePrefix="changeInteressentskab"
            idPrefix="change-is"
          />
        </div>
        <TextFormField
          name="changeInteressentskab.changeDate"
          label="Ændringsdato"
          id="change-is-input-change-date"
          isRequired
          type="date"
        />
        <RegistrationDateDisclosureNotice />
        <div className="blanket-two-col mt-4">
          <RegistrationContactFields
            namePrefix="changeInteressentskab"
            idPrefix="change-is"
          />
        </div>
      </FormSection>
    </>
  );
};
